import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Maximize2, Instagram, ArrowUpRight } from "lucide-react";
import { api, mediaUrl, imgUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import Magnetic from "@/components/Magnetic";
import Flipbook from "@/components/Flipbook";
import Lightbox from "@/components/Lightbox";
import KarmGallery from "@/components/KarmGallery";
import MagazineDeck from "@/components/MagazineDeck";

function Section({ label, children }) {
  return (
    <Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 md:py-14 border-t border-border">
        <div className="md:col-span-3">
          <p className="text-xs uppercase tracking-[0.25em] text-accent">{label}</p>
        </div>
        <div className="md:col-span-9">{children}</div>
      </div>
    </Reveal>
  );
}

export default function CaseStudy() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [coverTall, setCoverTall] = useState(false);
  const [lightbox, setLightbox] = useState(-1);
  const [coverZoom, setCoverZoom] = useState(false);

  useEffect(() => {
    api.get(`/projects/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="pt-44 text-center min-h-[60vh]">
      <p className="font-serif text-4xl">This story wandered off.</p>
      <Link to="/work" className="text-accent mt-6 inline-block">← Back to work</Link>
    </div>
  );
  if (!p) return <div className="pt-44 text-center text-muted2 min-h-[60vh]">Loading…</div>;

  const pdfs = (p.gallery || []).filter((m) => m.type === "pdf");
  const media = (p.gallery || []).filter((m) => m.type !== "pdf");
  const layout = p.gallery_layout || "grid";
  const grid = layout === "grid";
  const grid2 = layout === "grid2";
  const tiled = grid || grid2;
  // Feed layout helpers: keep original indices for the lightbox
  const mediaIx = media.map((m, i) => ({ ...m, _i: i }));
  const heroMedia = mediaIx.filter((m) => m.kind === "hero");
  const restMedia = mediaIx.filter((m) => m.kind !== "hero");
  const brandKey = (m) => m.brand || m.caption || "Brand";
  const isGrid = (m) => (m.kind || "grid") === "grid" && m.type !== "video";
  const isPost = (m) => m.kind === "post" || m.kind === "reel" || m.type === "video";
  const brandOrder = [...new Set(restMedia.map(brandKey))];
  const hasSections = restMedia.some((m) => m.section);
  const sectionOrder = [...new Set(restMedia.map((m) => m.section || "More"))];

  return (
    <div className="pt-32 md:pt-40">
      <div className="w-full max-w-[1600px] mx-auto px-5 md:px-10">
        <Link to="/work" className="text-sm text-muted2 hover:text-accent transition-colors" data-testid="back-to-work">← All work</Link>
        <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-muted2">
          <span className="rounded-full border border-border px-3 py-1 text-ink">{p.category}</span>
          <span>{p.client}</span><span>·</span><span>{p.year}</span>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif text-6xl md:text-8xl tracking-tighter leading-[0.85] mt-6"
          data-testid="case-study-title"
        >
          {p.title}
        </motion.h1>
        <p className="text-muted2 text-lg md:text-2xl mt-6 max-w-3xl leading-relaxed">{p.tagline}</p>

        {p.slug === "karm-108" && (
          <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="karm-links">
            <a href="https://karm108.com/" target="_blank" rel="noopener noreferrer"
              data-testid="karm-website-btn" data-cursor="hover"
              className="group inline-flex items-center gap-2 rounded-full bg-accent text-bg px-6 py-3 font-mono text-[11px] uppercase tracking-widest hover:bg-accent-hover transition-colors">
              Visit website
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a href="https://www.instagram.com/karm108official/" target="_blank" rel="noopener noreferrer"
              data-testid="karm-instagram-btn" data-cursor="hover"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 font-mono text-[11px] uppercase tracking-widest text-ink hover:border-lime hover:text-lime transition-colors">
              <Instagram size={15} /> @karm108official
            </a>
          </div>
        )}

        {p.slug === "exotic-india" && (
          <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="exotic-links">
            <a href="https://www.exoticindia.com/" target="_blank" rel="noopener noreferrer"
              data-testid="exotic-website-btn" data-cursor="hover"
              className="group inline-flex items-center gap-2 rounded-full bg-accent text-bg px-6 py-3 font-mono text-[11px] uppercase tracking-widest hover:bg-accent-hover transition-colors">
              Visit website
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a href="https://www.instagram.com/exoticindiaart/" target="_blank" rel="noopener noreferrer"
              data-testid="exotic-instagram-btn" data-cursor="hover"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 font-mono text-[11px] uppercase tracking-widest text-ink hover:border-lime hover:text-lime transition-colors">
              <Instagram size={15} /> @exoticindiaart
            </a>
          </div>
        )}
      </div>

      <div className="w-full max-w-[1600px] mx-auto px-5 md:px-10 mt-12">
        {pdfs.length > 0 ? (
          <div data-testid="flipbook-hero">
            {pdfs.map((m, i) =>
              p.slug === "karm-108" ? (
                <MagazineDeck key={i} title={m.caption || p.title} pages={(m.pages || []).map(mediaUrl)} />
              ) : (
                <Flipbook key={i} url={mediaUrl(m.url)} title={m.caption || p.title} pageImages={(m.pages || []).map(mediaUrl)} />
              )
            )}
          </div>
        ) : heroMedia.length > 0 ? (
          <div className="space-y-6" data-testid="hero-media">
            {heroMedia.map((m) => (
              <button key={m._i} onClick={() => setLightbox(m._i)} data-cursor="hover" data-testid={`hero-${m._i}`}
                className="group block mx-auto max-w-full overflow-hidden rounded-2xl bg-surface">
                <img src={imgUrl(m.url, 1600)} alt={m.caption || p.title}
                  onLoad={(e) => { e.target.parentElement.style.maxWidth = e.target.naturalWidth + "px"; }}
                  className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.01]" />
              </button>
            ))}
          </div>
        ) : (
          <motion.button
            initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}
            onClick={() => setCoverZoom(true)}
            data-cursor="hover" data-testid="cover-open"
            className={`group block overflow-hidden rounded-2xl bg-surface ${coverTall ? "mx-auto max-w-2xl" : "w-full"}`}
          >
            <img
              src={imgUrl(p.cover, 1600)}
              alt={p.title}
              onLoad={(e) => setCoverTall(e.target.naturalHeight > e.target.naturalWidth * 1.2)}
              className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.01]"
            />
          </motion.button>
        )}
      </div>

      <div className="w-full max-w-[1600px] mx-auto px-5 md:px-10 mt-16">
        {p.problem && <Section label="The problem"><p className="font-serif text-lg md:text-2xl leading-snug max-w-3xl">{p.problem}</p></Section>}
        {p.approach && <Section label="Our approach"><p className="text-muted2 text-base md:text-lg leading-relaxed max-w-3xl">{p.approach}</p></Section>}
        {p.deliverables?.length > 0 && (
          <Section label="Deliverables">
            <ul className="flex flex-wrap gap-1.5 max-w-2xl">
              {p.deliverables.map((d, i) => (
                <li key={i} className="rounded-full border border-border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wide text-ink whitespace-nowrap">{d}</li>
              ))}
            </ul>
          </Section>
        )}

        {p.slug === "karm-108" ? (
          <KarmGallery media={restMedia} onOpen={setLightbox} />
        ) : layout === "feed" ? (
          <Section label="The feeds">
            <div className="mb-8 flex items-center gap-3">
              <button
                onClick={() => setLightbox(0)}
                data-cursor="hover" data-testid="feed-view-all"
                className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-lime hover:text-bg transition-colors"
              >
                <Maximize2 size={13} /> View all {media.length} visuals
              </button>
              <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-widest text-muted2">{brandOrder.length} brands</span>
            </div>

            <div className="space-y-16">
              {brandOrder.map((b) => {
                const items = restMedia.filter((m) => brandKey(m) === b);
                const grids = items.filter(isGrid);
                const posts = items.filter(isPost);
                const handle = grids[0]?.caption || "";
                return (
                  <div key={b} data-testid={`brand-group-${b}`}>
                    <div className="mb-5 flex items-center gap-3">
                      <span className="h-11 w-11 shrink-0 rounded-full bg-gradient-to-tr from-accent via-lime to-accent p-[2px]">
                        <span className="flex h-full w-full items-center justify-center rounded-full bg-bg text-xs font-mono text-ink">
                          {b.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase() || "SM"}
                        </span>
                      </span>
                      <div className="min-w-0">
                        <div className="font-serif text-xl md:text-2xl leading-none text-ink truncate">{b}</div>
                        {handle && <div className="font-mono text-[11px] lowercase text-muted2 truncate">@{handle}</div>}
                      </div>
                      <span className="ml-auto text-[9px] font-mono uppercase tracking-widest text-lime">{grids.length ? `${grids.length} grid${grids.length > 1 ? "s" : ""}` : ""}{grids.length && posts.length ? " · " : ""}{posts.length ? `${posts.length} post${posts.length > 1 ? "s" : ""}` : ""}</span>
                    </div>

                    <div className="flex flex-wrap items-start gap-4 md:gap-5">
                      {grids.map((m) => (
                        <button key={m._i} onClick={() => setLightbox(m._i)} data-cursor="hover" data-testid={`feed-card-${m._i}`}
                          className="group w-full sm:flex-1 sm:min-w-[280px] sm:max-w-[420px] overflow-hidden rounded-2xl border border-border bg-surface transition-colors hover:border-lime">
                          <img src={imgUrl(m.url, 900)} alt={b} loading="lazy" className="w-full block transition-transform duration-700 group-hover:scale-[1.02]" />
                        </button>
                      ))}

                      {posts.length > 0 && (
                        <div className="flex-1 min-w-[240px] grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4 content-start">
                          {posts.map((m) => {
                            const isReel = m.kind === "reel" || m.type === "video";
                            return (
                              <button key={m._i} onClick={() => setLightbox(m._i)} data-cursor="hover" data-testid={`spotlight-${m._i}`}
                                className="group relative block w-full overflow-hidden rounded-xl border border-border bg-surface aspect-square">
                                {isReel
                                  ? <video src={mediaUrl(m.url)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                                  : <img src={imgUrl(m.url, 700)} alt={m.caption || b} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />}
                                {isReel && (
                                  <span className="absolute inset-0 flex items-center justify-center">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55 backdrop-blur text-white ring-1 ring-white/30 group-hover:scale-110 transition-transform">
                                      <Play size={18} className="ml-0.5" fill="currentColor" />
                                    </span>
                                  </span>
                                )}
                                {m.caption && <span className="absolute bottom-0 inset-x-0 px-2 py-1.5 text-[10px] font-mono text-white/90 bg-gradient-to-t from-black/70 to-transparent truncate text-left">{m.caption}</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        ) : hasSections ? (
          sectionOrder.map((sec) => {
            const [secLabel, secBlurb, secLayout] = sec.split("|");
            const wrap = secLayout === "stack"
              ? "flex flex-col gap-4 md:gap-6"
              : secLayout === "wide"
              ? "columns-1 md:columns-2 gap-3 md:gap-4 [&>*]:mb-3 md:[&>*]:mb-4"
              : "columns-1 sm:columns-2 lg:columns-3 gap-3 md:gap-4 [&>*]:mb-3 md:[&>*]:mb-4";
            return (
              <Section key={sec} label={secLabel}>
                {secBlurb && <p className="text-muted2 text-sm md:text-base mb-6 max-w-2xl leading-relaxed">{secBlurb}</p>}
                <div className={wrap}>
                  {restMedia.filter((m) => (m.section || "More") === sec).map((m) => (
                    <button
                      key={m._i}
                      onClick={() => setLightbox(m._i)}
                      data-cursor="hover" data-testid={`visual-${m._i}`}
                      className="group block w-full break-inside-avoid overflow-hidden rounded-xl bg-surface border border-border/60"
                    >
                      {m.type === "video"
                        ? <video src={mediaUrl(m.url)} className="w-full h-auto block" />
                        : <img src={imgUrl(m.url, 1400)} alt={m.caption || p.title} loading="lazy"
                            className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]" />}
                    </button>
                  ))}
                </div>
              </Section>
            );
          })
        ) : restMedia.length > 0 ? (
          <Section label="The visuals">
            <div className={grid ? "grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" : grid2 ? "grid grid-cols-2 gap-3 md:gap-4 max-w-[820px]" : "grid grid-cols-1 gap-6"}>
              {restMedia.map((m) => (
                <Reveal key={m._i} delay={(m._i % 3) * 0.05}>
                  <button
                    onClick={() => setLightbox(m._i)}
                    data-cursor="hover" data-testid={`visual-${m._i}`}
                    className={`group block overflow-hidden rounded-xl bg-surface ${tiled ? "w-full " + (grid ? "aspect-square" : "aspect-[3/4]") : "mx-auto max-w-full"}`}
                  >
                    {m.type === "video"
                      ? <video src={mediaUrl(m.url)} className={`w-full ${tiled ? "h-full object-cover" : ""}`} />
                      : <img
                          src={imgUrl(m.url, 1400)}
                          alt={m.caption || p.title}
                          onLoad={tiled ? undefined : (e) => { e.target.parentElement.style.maxWidth = e.target.naturalWidth + "px"; }}
                          className={`w-full ${tiled ? "h-full object-cover" : "block"} object-cover transition-transform duration-700 group-hover:scale-[1.02]`}
                        />}
                  </button>
                  {!tiled && m.caption && <p className="text-sm text-muted2 mt-2 text-center">{m.caption}</p>}
                </Reveal>
              ))}
            </div>
          </Section>
        ) : null}

        {p.results?.length > 0 && (
          <Section label="The outcome">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {p.results.map((r, i) => (
                <div key={i} className="rounded-xl bg-surface border border-border p-6">
                  <span className="text-accent font-serif text-3xl">0{i + 1}</span>
                  <p className="text-ink mt-3 leading-relaxed">{r}</p>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>

      <div className="w-full max-w-[1600px] mx-auto px-5 md:px-10 py-24 text-center border-t border-border mt-10">
        <p className="text-muted2 uppercase tracking-[0.3em] text-xs mb-6">Like what you see?</p>
        <p className="font-serif text-4xl md:text-6xl tracking-tight mb-10">Let's make yours next.</p>
        <Magnetic strength={0.4}>
          <Link to="/connect" className="inline-flex rounded-full bg-accent text-bg px-8 py-4 font-medium hover:bg-accent-hover transition-colors" data-testid="case-study-cta">
            Book a discovery call →
          </Link>
        </Magnetic>
      </div>

      <Lightbox items={media} index={lightbox} onClose={() => setLightbox(-1)} onIndex={setLightbox} />
      <Lightbox items={[{ url: p.cover, caption: p.title, type: "image" }]} index={coverZoom ? 0 : -1} onClose={() => setCoverZoom(false)} onIndex={() => {}} />
    </div>
  );
}
